/**
 * The **pack-opening flip-reveal modal** (trading card game, PRD #493 Slice 4,
 * stories 3/4): the five cards a just-opened pack contains, shown face-down, each
 * flipped one at a time on a click. A rarity-coloured burst plays on every flip,
 * while a **legendary** pull adds a gold flash + sound sting (`onLegendary`, wired
 * by the installer to the audio layer) so a big pull feels big.
 *
 * It holds **no game logic** (ADR-0268): the server already rolled the cards
 * (seeded, authoritative) and banked them into the collection via the accompanying
 * {@link CardStateMessage}; this modal is pure presentation over the owner-only
 * {@link PackOpenedMessage reveal}. It reuses the shared {@link renderCardFace} for
 * the revealed face, so a card looks identical here and in the Binder.
 *
 * A centred overlay (not a draggable window - it is a modal moment), built with
 * `textContent`-only DOM; a body-less document degrades to a harmless no-op handle.
 */
import { cardDef, type CardRarity } from './sim/index';
import type { WireDeckCard } from './net/protocol';
import { cardFaceModel, renderCardFace } from './cardFace';
import cardBackUrl from '../assets/ui/back.png?url';

/** Includes the final card's stagger so the modal closes after every card has landed. */
const COLLECT_ANIMATION_MS = 680;

/** Collaborators injected by the installer (kept thin so the modal is unit-testable). */
export interface PackOpeningDeps {
  /** The document to wire against; defaults to the global `document` (jsdom-testable). */
  doc?: Document;
  /** A legendary card was flipped: the installer plays the gold-flash sound sting. */
  onLegendary?: () => void;
}

/** The handle the installer keeps to show the modal when a pack opens. */
export interface PackOpeningHandle {
  /** Open the modal with these (five) freshly-rolled cards, all face-down. */
  reveal(cards: WireDeckCard[], duplicateGold?: number): void;
  close(): void;
  isOpen(): boolean;
}

/** `cardDef` that returns `null` instead of throwing on an unknown/legacy id (defensive over wire data). */
function safeCardDef(cardId: string, rarity: string) {
  try {
    return cardDef(cardId, rarity as CardRarity);
  } catch {
    return null;
  }
}

/** Build (or adopt) the `#pack-opening` modal; a body-less document degrades to a no-op. */
export function initPackOpening(deps: PackOpeningDeps): PackOpeningHandle {
  const doc = deps.doc ?? document;

  const noop: PackOpeningHandle = {
    reveal: () => {},
    close: () => {},
    isOpen: () => false,
  };
  if (!doc.body) return noop;

  // Build (or adopt) the overlay chrome once.
  let root = doc.getElementById('pack-opening');
  if (!root) {
    root = doc.createElement('div');
    root.id = 'pack-opening';
    root.hidden = true;
    root.setAttribute('aria-label', 'Opening a card pack');
    const panel = doc.createElement('div');
    panel.id = 'pack-opening-panel';
    const title = doc.createElement('h2');
    title.id = 'pack-opening-title';
    title.textContent = 'You open a pack!';
    const hint = doc.createElement('p');
    hint.id = 'pack-opening-hint';
    hint.textContent = 'Click each card to flip it.';
    const cards = doc.createElement('div');
    cards.id = 'pack-opening-cards';
    const done = doc.createElement('button');
    done.id = 'pack-opening-done';
    done.type = 'button';
    done.textContent = 'Collect';
    panel.append(title, hint, cards, done);
    root.append(panel);
    doc.body.append(root);
  }

  const cardsEl = doc.getElementById('pack-opening-cards');
  const hintEl = doc.getElementById('pack-opening-hint');
  const doneBtn = doc.getElementById('pack-opening-done') as HTMLButtonElement | null;
  if (!cardsEl || !hintEl || !doneBtn) return noop;

  const rootEl = root;
  let remaining = 0;
  let collectTimer: ReturnType<typeof setTimeout> | undefined;

  const resetCollection = (): void => {
    if (collectTimer !== undefined) {
      clearTimeout(collectTimer);
      collectTimer = undefined;
    }
    rootEl.classList.remove('collecting');
    doneBtn.disabled = false;
  };

  const close = (): void => {
    resetCollection();
    rootEl.hidden = true;
    cardsEl.replaceChildren();
  };

  const collect = (): void => {
    if (rootEl.hidden || rootEl.classList.contains('collecting')) return;
    if (doc.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      close();
      return;
    }
    rootEl.classList.add('collecting');
    doneBtn.disabled = true;
    doneBtn.textContent = 'Collecting...';
    collectTimer = setTimeout(() => {
      collectTimer = undefined;
      close();
    }, COLLECT_ANIMATION_MS);
  };

  doneBtn.addEventListener('click', collect);

  const reveal = (cards: WireDeckCard[], duplicateGold = 0): void => {
    resetCollection();
    cardsEl.replaceChildren();
    remaining = cards.length;
    const rebateText = duplicateGold > 0
      ? ` Duplicate rebate: ${duplicateGold.toLocaleString('en-US')} gold.`
      : '';
    hintEl.textContent = `Click each card to flip it.${rebateText}`;
    // The Collect button stays enabled throughout (a player may dismiss early), but
    // reads "Collect all" until every card is turned, then the plain "Collect".
    doneBtn.textContent = remaining === 0 ? 'Collect' : 'Collect all';

    for (const [index, card] of cards.entries()) {
      const slot = doc.createElement('div');
      slot.className = 'pack-card unflipped';
      slot.dataset.rarity = card.rarity;
      slot.style.setProperty('--pack-card-index', String(index));
      slot.style.setProperty('--pack-card-enter-delay', `${100 + index * 85}ms`);
      slot.style.setProperty('--pack-card-collect-delay', `${index * 45}ms`);

      const back = doc.createElement('button');
      back.className = 'card-back';
      back.type = 'button';
      back.setAttribute('aria-label', `Reveal card ${index + 1} of ${cards.length}`);
      const backArt = doc.createElement('img');
      backArt.src = cardBackUrl;
      backArt.alt = '';
      backArt.draggable = false;
      back.append(backArt);
      slot.append(back);

      let flipped = false;
      slot.addEventListener('click', () => {
        if (flipped) return;
        flipped = true;
        slot.classList.remove('unflipped');
        slot.classList.add('flipped', `burst-${card.rarity}`);
        const def = safeCardDef(card.cardId, card.rarity);
        slot.replaceChildren();
        if (def) {
          slot.append(renderCardFace(doc, cardFaceModel(def)));
        }
        if (card.rarity === 'legendary') {
          slot.classList.add('legendary-flash');
          deps.onLegendary?.();
        }
        remaining -= 1;
        if (remaining <= 0) {
          hintEl.textContent = `Nice pull! Your cards are in the Binder.${rebateText}`;
          doneBtn.textContent = 'Collect';
        }
      });

      cardsEl.append(slot);
    }
    rootEl.hidden = false;
  };

  return {
    reveal,
    close,
    isOpen: () => !rootEl.hidden,
  };
}
